<script>
import { formatDate } from '@/utils/dateUtils'
import { displayToast, handleResponseError } from '@/utils/toastUtils'
import { useNotificationStore } from '@/store/notificationStore'
import { confirmKeyAccountRepairFileOpening } from '@/utils/keyAccountRepairFileNavigationUtils'

const repairFileDetailUrlRegex =
  /^\/(?:repair-file\/detail|repair-file-detail)\/([^/?#]+)(?:[?#].*)?$/

export default {
  name: 'NotificationWidget',
  data() {
    return {
      notificationStore: useNotificationStore()
    }
  },
  async created() {
    await this.getNotificationList()
  },
  methods: {
    formatDate,
    async getNotificationList() {
      try {
        await this.notificationStore.refreshNotificationList()
      } catch (error) {
        handleResponseError(error)
      }
    },
    async loadMoreNotification() {
      try {
        await this.notificationStore.loadMoreNotification()
      } catch (error) {
        handleResponseError(error)
      }
    },
    async updateNotificationToOpen(notificationTechnicalId, notificationOpenStatus) {
      if (notificationOpenStatus === false) {
        try {
          await this.notificationStore.markNotificationOpen(notificationTechnicalId)
          return true
        } catch (error) {
          handleResponseError(error)
          return false
        }
      }
    },
    async updateNotificationToHidden(notificationTechnicalId) {
      try {
        const response = await this.notificationStore.hideNotification(notificationTechnicalId)

        if (response.technical_id === notificationTechnicalId) {
          return
        }
      } catch (error) {
        handleResponseError(error)
      }
    },
    async goToRedirect(notificationObject) {
      try {
        if (typeof notificationObject.redirection_url !== 'string') {
          displayToast(this.$t('ERROR_STATUS_CODE_MESSAGE.NOT_REDIRECT'), 'error')
          return
        }

        const regex = /^\/.*/
        const repairFileDetailMatch =
          notificationObject.redirection_url.match(repairFileDetailUrlRegex)

        if (repairFileDetailMatch) {
          const shouldOpen = await confirmKeyAccountRepairFileOpening({
            repairFile: notificationObject.repair_file,
            repairFileBoomerangId: repairFileDetailMatch[1],
            markAsRead:
              notificationObject.open === false
                ? () => this.notificationStore.markNotificationOpen(notificationObject.technical_id)
                : undefined
          })

          if (!shouldOpen) {
            return
          }

          const resolvedRoute = this.$router.resolve(notificationObject.redirection_url)
          await this.$router.push({
            name: 'repairFileDetail',
            params: { repairFileBoomerangId: repairFileDetailMatch[1] },
            query: resolvedRoute.query,
            hash: resolvedRoute.hash
          })
          // RepairFileDetail loads on creation; reload keeps notification jumps fresh on same-route navigation.
          window.location.reload()
          return
        }

        let openIsOk = false
        if (notificationObject.open === false) {
          openIsOk = await this.updateNotificationToOpen(
            notificationObject.technical_id,
            notificationObject.open
          )
        } else {
          openIsOk = true
        }

        if (openIsOk) {
          if (regex.test(notificationObject.redirection_url)) {
            await this.$router.push(notificationObject.redirection_url)
            // force reload page when you navigate in notification repair_file link
            window.location.reload()
          } else {
            window.open(notificationObject.redirection_url, '_blank', 'noopener,noreferrer')
          }
        } else {
          displayToast(this.$t('ERROR_STATUS_CODE_MESSAGE.NOT_REDIRECT'), 'error')
        }
      } catch (error) {
        handleResponseError(error)
        displayToast(this.$t('ERROR_STATUS_CODE_MESSAGE.NOT_REDIRECT'), 'error')
      }
    }
  }
}
</script>

<template>
  <v-card class="notification-container px-0 py-2">
    <v-card-text class="pa-0">
      <!--Notification loader-->
      <v-list
        v-if="notificationStore.loadingList"
        class="notification-container-list py-0 cursor-default"
        rounded
        width="300"
      >
        <v-list-item class="pa-0">
          <p class="text-center pa-4">
            {{ $t('NOTIFICATION.LOADING_NOTIFICATION') }}
          </p>
          <v-progress-linear class="my-5" indeterminate />
        </v-list-item>
      </v-list>

      <!--No Notification to show-->
      <v-list
        v-if="!notificationStore.loadingList && notificationStore.notificationList.length === 0"
        class="notification-container-list py-0 cursor-default"
        rounded
        width="300"
      >
        <v-list-item class="pa-0">
          <p class="text-center pa-4">
            {{ $t('NOTIFICATION.NO_NOTIFICATION') }}
          </p>
        </v-list-item>
      </v-list>

      <!--Notification List-->
      <v-list
        v-if="!notificationStore.loadingList && notificationStore.notificationList.length > 0"
        class="notification-container-list py-0"
        rounded
        width="300"
      >
        <!-- Notification Item -->
        <v-list-item
          v-for="(notification, index) of notificationStore.notificationList"
          :key="notification.technical_id"
          class="pa-0"
        >
          <v-divider v-if="index !== 0" color="white" thickness="2" />

          <div class="notification-row" :class="{ 'notification-row--unread': !notification.open }">
            <!--Title - X -->
            <div class="title-row">
              <!--Title-->
              <h1
                class="title"
                @click="updateNotificationToOpen(notification.technical_id, notification.open)"
              >
                {{ notification.title }}
              </h1>
              <!--X-->
              <v-btn-icon
                icon="mdi-close"
                size="x-small"
                variant="text"
                @click="updateNotificationToHidden(notification.technical_id)"
              />
            </div>
            <!--Subtitle-->
            <div
              v-if="notification.subtitle"
              class="subtitle-row"
              @click="updateNotificationToOpen(notification.technical_id, notification.open)"
            >
              <h2 class="subtitle">
                {{ notification.subtitle }}
              </h2>
            </div>
            <!--Content-->
            <div
              class="content-row"
              @click="updateNotificationToOpen(notification.technical_id, notification.open)"
            >
              <div class="content">
                {{ notification.content }}
              </div>
            </div>
            <!--Redirect-->
            <div v-if="notification.with_redirection" class="redirect-row">
              <div class="redirect-title" @click="goToRedirect(notification)">
                {{ notification.redirection_message }}
              </div>
            </div>
            <!--Date of creation-->
            <div
              class="date-row"
              @click="updateNotificationToOpen(notification.technical_id, notification.open)"
            >
              {{ formatDate(notification.created_at) }}
            </div>
          </div>
        </v-list-item>
      </v-list>
    </v-card-text>

    <!--Show more button-->
    <v-card-actions
      v-if="notificationStore.page < notificationStore.maxPages"
      class="justify-center pa-2"
    >
      <v-btn-secondary
        append-icon="mdi-chevron-down"
        :loading="notificationStore.loadingMore"
        :disabled="notificationStore.loadingMore"
        size="small"
        variant="text"
        @click="loadMoreNotification()"
      >
        {{ $t('NOTIFICATION.BUTTON.MORE') }}
      </v-btn-secondary>
    </v-card-actions>
  </v-card>
</template>

<style scoped>
.notification-container {
  max-height: 450px;
  overflow: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.notification-container::-webkit-scrollbar {
  display: none;
}

.notification-row {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-radius: var(--default-radius);
}

.notification-row--unread {
  background: rgba(var(--v-theme-primary), 0.12);
}

.title-row {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-content: center;
  align-items: center;
}

.title {
  font-weight: bold;
  font-size: 16px;
  word-wrap: break-word;
  max-width: 228px;
  line-height: normal;
}

.subtitle-row {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-content: center;
  justify-content: flex-start;
  align-items: center;
}

.subtitle {
  font-weight: bold;
  word-wrap: break-word;
  max-width: 255px;
  line-height: normal;
  font-size: small;
}

.content-row {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-content: center;
  justify-content: flex-start;
  align-items: center;
  font-size: small;
}

.content {
  word-wrap: break-word;
  max-width: 255px;
  line-height: normal;
}

.redirect-row {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-content: center;
  justify-content: flex-start;
  align-items: center;
}

.redirect-title {
  font-size: small;
  cursor: pointer;
  color: #ed7d31;
  word-wrap: break-word;
  max-width: 255px;
}

.date-row {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-content: center;
  justify-content: flex-start;
  align-items: center;
  font-size: x-small;
}
</style>
